import { useState, useEffect, useCallback } from 'react';
import { API_TIMEOUT, buildApiUrl } from '../config';

const apiRequest = async (endpoint, options = {}) => {
  const url = buildApiUrl(endpoint);
  const defaultHeaders = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };
  
  const config = {
    headers: { ...defaultHeaders, ...options.headers },
    ...options
  };
  
  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.mensaje || `HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    return {
      success: true,
      data,
      error: data.error,
      mensaje: data.mensaje
    };
    
  } catch (error) {
    throw error;
  }
};

export const useEmpresas = (options = {}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const {
    immediate = false
  } = options;

  const fetchEmpresas = useCallback(async () => {
    setLoading(true);
    setError(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

    try {
      const response = await fetch(buildApiUrl('empresas'), {
        signal: controller.signal,
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      });

      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const validatedData = result.data || result;
      setData(validatedData);
      return validatedData;
    } catch (err) {
      const errorMessage = err.name === 'AbortError' 
        ? 'Tiempo de espera agotado' 
        : 'Error de conexión con el servidor';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (immediate) {
      fetchEmpresas();
    }
  }, [immediate, fetchEmpresas]);

  return {
    data,
    loading,
    error,
    refetch: fetchEmpresas,
  };
};

export const getEmpresaById = async (id) => {
  return apiRequest(`empresa/${id}`, { method: 'GET' });
};

export const createEmpresa = async (empresaData) => {
  const dataToSend = {
    nombre: empresaData.Nombre || empresaData.nombre,
    nombreEstado: empresaData.NombreEstado || empresaData.nombreEstado,
    promocion: empresaData.Promocion || empresaData.promocion,
    ubicacion: empresaData.Ubicacion || empresaData.ubicacion,
    url_File: empresaData.Url_File || empresaData.url_File,
    fileName: empresaData.FileName || empresaData.fileName
  };
  return apiRequest('empresa', {
    method: 'POST',
    body: JSON.stringify(dataToSend)
  });
};

export const updateEmpresa = async (id, empresaData) => {
  const dataToSend = {
    nombre: empresaData.Nombre || empresaData.nombre,
    nombreEstado: empresaData.NombreEstado || empresaData.nombreEstado,
    promocion: empresaData.Promocion || empresaData.promocion,
    ubicacion: empresaData.Ubicacion || empresaData.ubicacion,
    url_File: empresaData.Url_File || empresaData.url_File,
    fileName: empresaData.FileName || empresaData.fileName
  };
  return apiRequest(`empresa/${id}`, {
    method: 'PUT',
    body: JSON.stringify(dataToSend)
  });
};

export const deleteEmpresa = async (id) => {
  return apiRequest(`empresa/${id}`, {
    method: 'DELETE'
  });
};

export default {
  useEmpresas,
  getEmpresaById,
  createEmpresa,
  updateEmpresa,
  deleteEmpresa
};
